import React, { useEffect, useState } from 'react'

import {
  BenefitParticipationDecision,
  CompanyUserBenefitDetailsResponse,
  PlanPeriodDto,
  benefitPlanPeriods,
  getCompanyUserBenefitDetails,
  getRenewalPlanPeriod,
} from 'core'

import { Alert, BenefitPlanPeriodDropDown, Button, Card, Col, Row, Skeleton } from 'common/components'
import { calculateDays, formatYear, formatYearV2, nowV2, parseZoneDayMonthYearFormat } from 'common/dateFormatters'
import { useLoginUserV2, useQueryParams, useQueryRequestV2 } from 'common/hooks'

import CountdownTimer from '../../../adminDashboard/home/common/countdownTimer'
import { ROUTE_PATHS } from '../../../common/routePaths'
import OptInDashboard from './optInDashboard'
import OptOutDashboard from './optOutDashboard'
import OptInOptOutDecision from './optinOptOutDecision'
import RenewalOptInOptOutDecision from './renewalOptInOptOutDecision'

const IchraEEDashboardHome = (): JSX.Element => {
  const queryParams = useQueryParams()
  const planPeriodIdQueryParam = queryParams.get('planPeriodId')

  const { user } = useLoginUserV2()
  const [selectedPlanPeriod, setSelectedPlanPeriod] = useState<PlanPeriodDto | null>()

  const { data: renewalPlanPeriod } = useQueryRequestV2({ initialState: null, queryFn: () => getRenewalPlanPeriod() })

  const isRenewalPlanPeriod = renewalPlanPeriod?.id === selectedPlanPeriod?.id

  const days = renewalPlanPeriod ? calculateDays(nowV2(), renewalPlanPeriod.startDate) : 0

  const {
    data: companyUserBenefitDetails,
    isLoading: isCompanyUserBenefitDetailsLoading,
    sendQuery: sendGetCompanyUserBenefitDetails,
  } = useQueryRequestV2<CompanyUserBenefitDetailsResponse>({})

  const { data: renewalCompanyUserBenefitDetails, sendQuery: sendGetRenewalCompanyUserBenefitDetails } =
    useQueryRequestV2<CompanyUserBenefitDetailsResponse>({})

  const { data: planPeriods } = useQueryRequestV2({
    queryFn: () =>
      benefitPlanPeriods({
        onlyPlanPeriodsWithNotificationDateDefined: true,
      }),
  })

  useEffect(() => {
    if (planPeriods && planPeriods.length > 0) {
      const found = planPeriodIdQueryParam
        ? planPeriods.find((x) => x.id === planPeriodIdQueryParam)
        : planPeriods.find((x) => x.active == true) ?? planPeriods[0]

      if (found) {
        setSelectedPlanPeriod(found)
      }
    }
  }, [planPeriods])

  useEffect(() => {
    ;(async () => {
      if (selectedPlanPeriod) {
        await sendGetCompanyUserBenefitDetails(() => getCompanyUserBenefitDetails(selectedPlanPeriod.id))
      }
    })()
  }, [selectedPlanPeriod])

  useEffect(() => {
    ;(async () => {
      if (renewalPlanPeriod && renewalPlanPeriod.notificationDate) {
        await sendGetRenewalCompanyUserBenefitDetails(() => getCompanyUserBenefitDetails(renewalPlanPeriod.id))
      }
    })()
  }, [renewalPlanPeriod])

  const onPlanPeriodChanged = (planPeriodId: string) => {
    setSelectedPlanPeriod(planPeriods.find((x) => x.id === planPeriodId))
  }

  const renewalPlanYear = renewalPlanPeriod ? formatYearV2(renewalPlanPeriod.startDate) : ''

  return (
    <Skeleton active={true} loading={!planPeriods}>
      {planPeriods && (
        <div data-testid={'ichra-ee-dashboard'}>
          {renewalPlanPeriod &&
            renewalCompanyUserBenefitDetails &&
            companyUserBenefitDetails &&
            companyUserBenefitDetails.terminatedInCurrentPlanPeriod === false &&
            renewalCompanyUserBenefitDetails.benefitParticipationDecision ==
              BenefitParticipationDecision.NoDecision && (
              <Card className={'mb-16'} style={{ backgroundColor: '#E6FFFF', border: '1px solid #8DF3FC' }}>
                <Row gutter={16}>
                  <Col>
                    <CountdownTimer
                      days={days}
                      defaultContentForMoreThanZeroDays={`Days left until your benefit starts`}
                      defaultContentForZeroDays={'No more days left'}
                    />
                  </Col>

                  <Col xxl={18} xl={16} lg={13} md={13} sm={13} xs={24}>
                    <h4>Congratulations! You have an ICHRA offer to review for {renewalPlanYear}.</h4>
                    <p>
                      {user?.companyName} has offered you a benefit as part of its Individual Coverage HRA (ICHRA)
                      benefit for {renewalPlanYear}
                    </p>

                    <p>
                      You need to confirm or change your ICHRA participation decision by{' '}
                      {parseZoneDayMonthYearFormat(
                        renewalCompanyUserBenefitDetails.maxDateToTakeBenefitParticipationDecision,
                      )}
                      , at which time your new benefit will launch and your decision will be locked-in.
                    </p>

                    <ul>
                      <li>
                        Note: If you choose not to make a decision for {renewalPlanYear}, your current{' '}
                        {companyUserBenefitDetails.currentBenefitParticipationDecision?.name} decision will be applied
                        to the new benefit period.
                      </li>
                    </ul>

                    {selectedPlanPeriod?.id !== renewalPlanPeriod?.id && (
                      <Row gutter={16} className="mb-24 mt-24">
                        <Col>
                          <Button
                            type="primary"
                            onClick={() => {
                              window.location.href = `${ROUTE_PATHS.ELIGIBLE_EMPLOYEE_DASHBOARD}?planPeriodId=${renewalPlanPeriod.id}`
                            }}
                          >
                            Confirm your status for {renewalPlanYear}
                          </Button>
                        </Col>
                      </Row>
                    )}
                  </Col>
                </Row>
              </Card>
            )}

          <Row justify="end" className={'w-100 font-16 text-light-bold mb-16'}>
            <>
              {planPeriods && (
                <BenefitPlanPeriodDropDown
                  defaultValue={planPeriodIdQueryParam}
                  planPeriods={planPeriods}
                  onPlanPeriodSelected={onPlanPeriodChanged}
                />
              )}
            </>
          </Row>

          <div className="mb-16">
            <Skeleton active={true} loading={isCompanyUserBenefitDetailsLoading}>
              {companyUserBenefitDetails && companyUserBenefitDetails.isEligible && (
                <>
                  {companyUserBenefitDetails.benefitParticipationDecision === BenefitParticipationDecision.NoDecision &&
                    !isRenewalPlanPeriod && (
                      <OptInOptOutDecision companyUserBenefitDetails={companyUserBenefitDetails} />
                    )}

                  {companyUserBenefitDetails.benefitParticipationDecision === BenefitParticipationDecision.NoDecision &&
                    isRenewalPlanPeriod && (
                      <RenewalOptInOptOutDecision
                        companyUserBenefitDetails={companyUserBenefitDetails}
                        renewalPlanPeriod={renewalPlanPeriod}
                      />
                    )}

                  {companyUserBenefitDetails.benefitParticipationDecision === BenefitParticipationDecision.OptedIn && (
                    <OptInDashboard companyUserBenefitDetails={companyUserBenefitDetails} />
                  )}

                  {companyUserBenefitDetails.benefitParticipationDecision === BenefitParticipationDecision.OptedOut && (
                    <OptOutDashboard companyUserBenefitDetails={companyUserBenefitDetails} />
                  )}
                </>
              )}

              {companyUserBenefitDetails && !companyUserBenefitDetails.isEligible && (
                <Card className={'opt-out-dashboard-wrap'}>
                  <p>Welcome, {user.firstName}</p>
                  <h2 className={'text-bold'}>What’s new for today</h2>
                  <Alert
                    type={'info'}
                    message={
                      <>
                        You are currently <b>INELIGIBLE</b> for the ICHRA provided by <b>{user.companyName}</b> for{' '}
                        <b>{formatYear(companyUserBenefitDetails.planPeriodStartDate)}</b>. If you believe this is in
                        error, please contact your benefits administrator.
                      </>
                    }
                  ></Alert>
                </Card>
              )}
            </Skeleton>
          </div>
        </div>
      )}
    </Skeleton>
  )
}

export default IchraEEDashboardHome
